import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  EVENT_CATEGORIES,
  EVENT_CATEGORY_LABELS,
  COMMISSION_TYPES,
  COMMISSION_TYPE_LABELS,
  COUNTING_MODES,
  COUNTING_MODE_LABELS,
  RECURRING_MODES,
} from '@affily/shared/constants';
import { describeEvent } from '@affily/shared/commission';
import { percentToBps, bpsToPercent } from '@affily/shared/money';
import { api } from '../../lib/api.js';
import { rule, nestedError } from '../../lib/fieldRules.js';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { TrackingActivity } from '../../components/StackPicker.jsx';
import { StoreConnect } from '../../components/StoreConnect.jsx';
import { CreatorCodesSettings } from '../../components/CreatorCodesSettings.jsx';
import { ProgramSteps, NextStep } from '../../components/ProgramSteps.jsx';
import {
  Button,
  Field,
  Input,
  Select,
  Textarea,
  Alert,
  Badge,
  Spinner,
  SectionTitle,
} from '../../components/ui.jsx';

/**
 * Conversion actions and the integration snippet.
 *
 * Kept on its own page rather than crammed into the program editor: defining
 * what counts as a conversion is a different job, done once, usually by a
 * different person than the one setting the commission rate.
 */
/** A form row as the API takes it: the summary line is computed from the same shape. */
function toPayloadEvent(e) {
  return {
    key: e.key,
    name: e.name,
    description: e.description || undefined,
    category: e.category ?? 'custom',
    isPayable: Boolean(e.isPayable),
    commissionType: e.isPayable ? (e.commissionType ?? null) : null,
    rateBps: e.isPayable && e.ratePercent !== '' ? percentToBps(Number(e.ratePercent)) : null,
    flatAmountCents:
      e.isPayable && e.flatAmount !== '' ? Math.round(Number(e.flatAmount) * 100) : null,
    recurringMode: e.isPayable ? (e.recurringMode ?? 'one_time') : 'one_time',
    recurringMonths:
      e.isPayable && e.recurringMode === 'fixed_period' && e.recurringMonths !== ''
        ? Number(e.recurringMonths)
        : null,
    countingMode: e.countingMode ?? 'every',
    isActive: e.isActive !== false,
    manualOnly: Boolean(e.manualOnly),
  };
}

export default function ProgramEvents() {
  const { id } = useParams();
  // Set by the program editor when it sends a just-saved program here.
  const [params] = useSearchParams();
  const from = params.get('from');
  const programs = useFetch('/programs/mine/list');
  const integration = useFetch(`/programs/${id}/integration`);

  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);
  // The one action whose form is open; the rest show as a one-line summary.
  const [editing, setEditing] = useState(null);

  const program = programs.data?.programs?.find((p) => p.id === id) ?? null;

  useEffect(() => {
    if (!program) return;
    setEvents(
      (program.events ?? []).map((e) => ({
        ...e,
        ratePercent: e.rateBps != null ? String(bpsToPercent(e.rateBps)) : '',
        flatAmount: e.flatAmountCents != null ? String(e.flatAmountCents / 100) : '',
        recurringMonths: e.recurringMonths ?? '',
      })),
    );
  }, [program]);

  const update = (i, patch) =>
    setEvents((list) => list.map((e, idx) => (idx === i ? { ...e, ...patch } : e))) ||
    setSaved(false);

  /** Saves the whole table; true when it went through. */
  async function save(list = events) {
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      await api.patch(`/programs/${id}`, { events: list.map(toPayloadEvent) });
      setSaved(true);
      setEditing(null);
      programs.reload();
      integration.reload();
      return true;
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.details ?? {});
      return false;
    } finally {
      setBusy(false);
    }
  }

  // Back to what is saved: the effect above rebuilds the rows from the program.
  function cancel() {
    setEditing(null);
    setFieldErrors({});
    programs.reload();
  }

  async function copy(text, key) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setError('Could not copy — select the text and copy it manually.');
    }
  }

  if (programs.loading) {
    return (
      <DashboardLayout side="brand">
        <Spinner />
      </DashboardLayout>
    );
  }

  const int = integration.data;
  const tracked =
    (int?.connections ?? []).some((c) => c.status === 'active') ||
    Object.values(int?.platforms ?? {}).some((p) => p.connected);

  return (
    <DashboardLayout
      side="brand"
      title="Tracking & stores"
      description="How sales reach Affily, what counts as a conversion, and what each one pays."
    >
      <ProgramSteps programId={id} current="tracking" program={program} />
      <div className="max-w-3xl space-y-8">
        <Alert>{error}</Alert>
        {saved && <Alert tone="positive">Saved.</Alert>}
        {Object.keys(fieldErrors).length > 0 && (
          <Alert>
            {Object.entries(fieldErrors).map(([k, v]) => (
              <span key={k} className="block">
                {v}
              </span>
            ))}
          </Alert>
        )}

        {(from === 'created' || from === 'published') && program && (
          <LaunchCheck program={program} from={from} tracked={int ? tracked : null} />
        )}

        {/* Platform connections lead: no code beats any amount of good code. */}
        <StoreConnect
          programId={id}
          platforms={int?.platforms}
          stacks={int?.stacks ?? []}
          onChanged={integration.reload}
        />

        <CreatorCodesSettings programId={id} />

        <TrackingActivity programId={id} events={program?.events ?? []} />

        {/* ── Actions ───────────────────────────────────────────────── */}
        <section>
          <SectionTitle hint="Everything here is published on your public program page">
            Actions
          </SectionTitle>

          <div className="space-y-2">
            {events.map((e, i) => {
              const isPrimary = e.key === program?.primaryEventKey;
              const reportedByStore = tracked && e.category === 'purchase' && !e.manualOnly;

              if (editing !== i) {
                const pays = isPrimary
                  ? (program?.termsSummary ?? 'Headline terms')
                  : describeEvent(toPayloadEvent(e), program);
                return (
                  <div
                    key={i}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-rule bg-paper-raised px-4 py-3"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{e.name || e.key}</span>
                        {isPrimary && <Badge tone="accent">Headline</Badge>}
                        {e.manualOnly && <Badge tone="caution">Recorded by you</Badge>}
                      </div>
                      <p className="mt-0.5 text-sm text-ink-muted">
                        {pays} · {COUNTING_MODE_LABELS[e.countingMode ?? 'every']}
                        {reportedByStore && ' · reported by your store'}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(i)}
                      disabled={busy || (editing !== null && editing !== i)}
                    >
                      Edit
                    </Button>
                  </div>
                );
              }

              return (
                <div key={i} className="rounded-lg border border-accent/40 bg-paper-raised p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="rounded bg-paper-sunken px-1.5 py-0.5 text-xs" title={rule.eventKey()}>
                        {e.key}
                      </code>
                      {isPrimary && <Badge tone="accent">Headline action</Badge>}
                      {!e.isPayable && <Badge>Tracked only</Badge>}
                      {e.manualOnly && <Badge tone="caution">Recorded by you</Badge>}
                    </div>
                    {!isPrimary && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => save(events.filter((_, x) => x !== i))}
                        className="text-xs text-negative hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <Field
                      label="Name"
                      required
                      error={nestedError(fieldErrors, "events", i, "name")}
                      constraint={rule.max(80)}
                    >
                      <Input
                        value={e.name ?? ''}
                        onChange={(ev) => update(i, { name: ev.target.value })}
                        maxLength={80}
                      />
                    </Field>
                    <Field
                      label="Kind"
                      hint="What sort of action this is, for labels and the funnel."
                      error={nestedError(fieldErrors, "events", i, "category")}
                    >
                      <Select
                        value={e.category ?? 'custom'}
                        onChange={(ev) => update(i, { category: ev.target.value })}
                      >
                        {EVENT_CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {EVENT_CATEGORY_LABELS[c]}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>

                  <Field
                    label="Description"
                    className="mt-3"
                    error={nestedError(fieldErrors, "events", i, "description")}
                    constraint={rule.max(400)}
                    hint="Shown to creators."
                  >
                    <Textarea
                      rows={2}
                      value={e.description ?? ''}
                      onChange={(ev) => update(i, { description: ev.target.value })}
                      maxLength={400}
                    />
                  </Field>

                  <Field
                    label="Counts"
                    className="mt-3"
                    hint="Every time it happens, or only the first time per referred person — right for a signup bonus."
                  >
                    <Select
                      value={e.countingMode ?? 'every'}
                      onChange={(ev) => update(i, { countingMode: ev.target.value })}
                    >
                      {COUNTING_MODES.map((m) => (
                        <option key={m} value={m}>
                          {COUNTING_MODE_LABELS[m]}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <label className="mt-3 flex items-start gap-2.5">
                    <input
                      type="checkbox"
                      checked={Boolean(e.manualOnly)}
                      onChange={(ev) => update(i, { manualOnly: ev.target.checked })}
                      className="mt-0.5 size-4 accent-accent"
                    />
                    <span>
                      <span className="block text-sm font-medium">Only I record this, in Affily</span>
                      <span className="block text-xs text-ink-muted">
                        For something that happens offline, like a sale signed after a viewing. Your
                        site’s tag and postbacks can’t report it — anyone can fire a tag — and you
                        record it from a tracked action in the activity above, credited to the
                        creator who brought it.
                      </span>
                    </span>
                  </label>

                  {isPrimary ? (
                    <p className="mt-3 rounded-md border border-rule bg-paper-sunken px-3 py-2 text-xs text-ink-muted">
                      This action carries the program’s headline terms. Change its rate on the{' '}
                      <Link
                        to={`/dashboard/brand/programs/${id}`}
                        className="text-accent hover:underline"
                      >
                        program page
                      </Link>{' '}
                      — one place to edit, so the rate line and this table can’t disagree.
                    </p>
                  ) : program?.commissionType === 'gift_only' ? (
                    <p className="mt-3 rounded-md border border-rule bg-paper-sunken px-3 py-2 text-xs text-ink-muted">
                      This is a gift-only program, so every action is tracked but none pays a
                      commission.
                    </p>
                  ) : (
                    <>
                      <label className="mt-3 flex items-start gap-2.5">
                        <input
                          type="checkbox"
                          checked={Boolean(e.isPayable)}
                          onChange={(ev) => update(i, { isPayable: ev.target.checked })}
                          className="mt-0.5 size-4 accent-accent"
                        />
                        <span>
                          <span className="block text-sm font-medium">Pays a commission</span>
                          <span className="block text-xs text-ink-muted">
                            Leave off to track the action for funnel visibility without paying for
                            it.
                          </span>
                        </span>
                      </label>

                      {e.isPayable && (
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          <Field label="Pays">
                            <Select
                              value={e.commissionType ?? ''}
                              onChange={(ev) =>
                                update(i, { commissionType: ev.target.value || null })
                              }
                            >
                              <option value="">Same as headline terms</option>
                              {COMMISSION_TYPES.filter((t) => t !== 'flat_per_post' && t !== 'gift_only').map((t) => (
                                <option key={t} value={t}>
                                  {COMMISSION_TYPE_LABELS[t]}
                                </option>
                              ))}
                            </Select>
                          </Field>

                          {e.commissionType === 'percent_of_sale' ? (
                            <Field
                            label="Rate (%)"
                            error={nestedError(fieldErrors, "events", i, "rateBps")}
                            constraint={rule.percent()}
                          >
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={e.ratePercent ?? ''}
                                onChange={(ev) => update(i, { ratePercent: ev.target.value })}
                              />
                            </Field>
                          ) : e.commissionType === 'flat_per_conversion' ? (
                            <Field
                              label={`Amount (${(program?.currency ?? 'usd').toUpperCase()})`}
                              error={nestedError(fieldErrors, "events", i, "flatAmountCents")}
                              constraint={rule.money()}
                            >
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={e.flatAmount ?? ''}
                                onChange={(ev) => update(i, { flatAmount: ev.target.value })}
                              />
                            </Field>
                          ) : null}

                          <Field label="Recurring">
                            <Select
                              value={e.recurringMode ?? 'one_time'}
                              onChange={(ev) => update(i, { recurringMode: ev.target.value })}
                            >
                              {RECURRING_MODES.map((m) => (
                                <option key={m} value={m}>
                                  {m === 'one_time' ? 'One-time' : 'Recurring'}
                                </option>
                              ))}
                            </Select>
                          </Field>

                          {e.recurringMode === 'fixed_period' && (
                            <Field
                              label="Billing cycles"
                              error={nestedError(fieldErrors, "events", i, "recurringMonths")}
                              constraint={rule.recurringCycles()}
                            >
                              <Input
                                type="number"
                                min="1"
                                max="60"
                                value={e.recurringMonths ?? ''}
                                onChange={(ev) => update(i, { recurringMonths: ev.target.value })}
                              />
                            </Field>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {e.manualOnly ? (
                    <p className="mt-3 border-t border-rule pt-3 text-xs text-ink-muted">
                      Recorded by you from the activity above — there is nothing to install.
                    </p>
                  ) : reportedByStore ? (
                    <p className="mt-3 border-t border-rule pt-3 text-xs text-ink-muted">
                      Orders from your connected store count as this action — there is nothing to
                      install.
                    </p>
                  ) : (
                    <div className="mt-3 border-t border-rule pt-3">
                      <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                        Report it from your site
                      </p>
                      <code className="mt-1 block overflow-x-auto rounded bg-paper-sunken px-2 py-1.5 text-xs">
                        {`affily.track('${e.key}', { ${
                          (e.countingMode ?? 'every') === 'once_per_visitor'
                            ? 'customerId: user.id'
                            : 'orderId: order.id, amountCents: order.totalCents'
                        } });`}
                      </code>
                      {e.category === 'purchase' && (
                        <p className="mt-1.5 text-xs text-ink-muted">
                          That is the browser tag. For anything that pays money, send{' '}
                          <code>event={e.key}</code> with the signed postback instead — see the
                          signing secret below.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-3 border-t border-rule pt-3">
                    <Button size="sm" onClick={() => save()} disabled={busy}>
                      {busy ? 'Saving…' : 'Save'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={cancel} disabled={busy}>
                      Cancel
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {editing === null && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => {
                setEvents((list) => [
                  ...list,
                  {
                    key: `action_${list.length + 1}`,
                    name: 'New action',
                    category: 'custom',
                    isPayable: false,
                    countingMode: 'every',
                    recurringMode: 'one_time',
                    isActive: true,
                  },
                ]);
                setEditing(events.length);
                setSaved(false);
              }}
            >
              Add action
            </Button>
          )}

          <p className="mt-3 text-xs text-ink-faint">
            Changing an action never affects partnerships that already exist — each one keeps the
            table it was accepted under.
          </p>
        </section>

        {/* ── Server-to-server ──────────────────────────────────────── */}
        {int && (
          <section>
            <SectionTitle hint="Use this for anything that pays money">
              Signing secret
            </SectionTitle>
            <div className="rounded-lg border border-caution/30 bg-caution-soft p-4">
              <code className="block overflow-x-auto text-xs">{int.postbackSecret}</code>
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={() => copy(int.postbackSecret, 'secret')}
              >
                {copied === 'secret' ? 'Copied' : 'Copy secret'}
              </Button>
              <p className="mt-2 text-xs text-ink-muted">
                Keep this on your server. Anyone holding it can report conversions against your
                budget. A browser tag is blockable and cannot carry a secret, so use the signed
                postback for purchases and renewals.
              </p>
            </div>
          </section>
        )}

        <NextStep programId={id} after="tracking" />
      </div>
    </DashboardLayout>
  );
}

/**
 * The check a brand lands on right after saving or publishing: is it live,
 * where do creators' links send people, and will the sales come back.
 *
 * `tracked` is null while the integration is still loading, so the panel never
 * says "not tracking" about a store that is in fact connected.
 */
function LaunchCheck({ program, from, tracked }) {
  const live = program.status === 'active';
  const dest = program.destinationUrl ?? '';
  // What /go/:code redirects to (buildDestination on the server).
  const landing = dest && `${dest}${dest.includes('?') ? '&' : '?'}aff_click=…&aff_ref=CREATORCODE`;

  return (
    <section className="space-y-3 rounded-lg border border-rule p-4">
      <SectionTitle hint={live ? 'Creators can apply now' : 'Saved as a draft'}>
        {from === 'published' ? 'Program published' : 'Program saved'} — check before creators arrive
      </SectionTitle>

      <dl className="space-y-3 text-sm">
        <div>
          <dt className="text-ink-muted">Creators’ links land on</dt>
          <dd className="mt-1">
            <code className="block overflow-x-auto rounded bg-paper-sunken px-2 py-1 text-xs">
              {landing || 'No destination URL set'}
            </code>
            <span className="mt-1 block text-xs text-ink-faint">
              Open it yourself and make sure the page loads with the extra parameters. Wrong
              page?{' '}
              <Link to={`/dashboard/brand/programs/${program.id}`} className="text-accent hover:underline">
                Change the destination
              </Link>
              .
            </span>
          </dd>
        </div>
        <div>
          <dt className="text-ink-muted">Sales reported</dt>
          <dd className="mt-1">
            {tracked === null ? (
              <Spinner />
            ) : tracked ? (
              <Badge tone="positive">Tracking is on</Badge>
            ) : (
              <Alert tone="caution">
                Nothing reports sales to this program yet, so creators would earn nothing. Connect
                a store or install tracking below, then send a test conversion.
              </Alert>
            )}
          </dd>
        </div>
      </dl>

      {!live && (
        <p className="text-xs text-ink-faint">
          Drafts aren’t listed and nobody can apply. Publish in{' '}
          <Link to={`/dashboard/brand/programs/${program.id}/publish`} className="text-accent hover:underline">
            step 4
          </Link>{' '}
          once tracking is in place.
        </p>
      )}
    </section>
  );
}

export { describeEvent };
